/**
 * Consultas e gravações da agenda pelo TanStack Query.
 *
 * Papel: aqui ficam `useContatos` e `useCategorias`, que mantêm em cache o que
 * a tela lê, e `useSalvarContato` e `useExcluirContato`, que gravam e mandam a
 * listagem se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_MAILING`, e as duas
 * mutações invalidam esse prefixo inteiro em vez da página aberta — o mesmo
 * contato aparece em outras páginas e em outras buscas já guardadas no cache, e
 * uma chave que não começasse pelo prefixo continuaria mostrando o dado antigo
 * depois de salvar.
 *
 * A invalidação alcança também `CHAVE_CATEGORIAS`, porque salvar um contato com
 * categoria nova é o que faz surgir um rótulo que a lista de sugestão ainda não
 * conhece.
 */
import { type NovoContatoMailing, type ParametrosListagem } from '@concierge/shared';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  atualizarContato,
  buscarCategorias,
  buscarContatos,
  criarContato,
  excluirContato,
} from '../api/contatos';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_MAILING = ['mailing'] as const;

/** Chave do catálogo de categorias, que não depende de parâmetro. */
export const CHAVE_CATEGORIAS = [...CHAVE_MAILING, 'categorias'] as const;

/**
 * Lê uma página da agenda.
 *
 * A página anterior continua na tela enquanto a seguinte carrega, pelo
 * `keepPreviousData`. Sem isso a lista piscaria vazia a cada troca de página e a
 * cada letra digitada na busca.
 *
 * @param parametros - Página, tamanho e busca; cada combinação ocupa uma entrada
 *   própria no cache.
 * @returns Resultado da consulta; `isPlaceholderData` verdadeiro indica que o
 *   que está na tela ainda é a página anterior.
 */
export function useContatos(parametros: ParametrosListagem) {
  return useQuery({
    queryKey: [...CHAVE_MAILING, 'lista', parametros],
    queryFn: ({ signal }) => buscarContatos(parametros, signal),
    placeholderData: keepPreviousData,
  });
}

/**
 * Lê as categorias em uso, para a lista de sugestão do formulário.
 *
 * @returns Resultado da consulta, com as categorias distintas em ordem.
 */
export function useCategorias() {
  return useQuery({
    queryKey: CHAVE_CATEGORIAS,
    queryFn: ({ signal }) => buscarCategorias(signal),
  });
}

/** Variáveis de `useSalvarContato`. */
interface SalvarContatoVariaveis {
  /** Identificador do contato a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: NovoContatoMailing;
}

/**
 * Cria ou altera um contato, conforme venha identificador.
 *
 * É uma mutação só para os dois casos porque o formulário é o mesmo, e separar
 * obrigaria a tela a escolher qual chamar antes de saber se está editando.
 *
 * O `mutateAsync` rejeita com `ErroDaApi` quando a API recusa, e o formulário
 * precisa tratar essa rejeição para mostrar a mensagem; o sucesso já invalida a
 * listagem e o catálogo por conta própria.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarContato() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarContatoVariaveis) =>
      id === null ? criarContato(dados) : atualizarContato(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_MAILING }),
  });
}

/**
 * Apaga um contato da agenda.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirContato() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirContato(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_MAILING }),
  });
}
